import styled from "@emotion/styled"
import { PButton } from "@porsche-design-system/components-react"
import { spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import type { WarrantyPublicOfferView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useMemo, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { porscheApprovedMessages } from "src/common/messages/porscheApproved.messages"
import { ButtonGroup } from "src/components"
import { isDealerDecisionValid } from "src/screens/common/porscheApproved/isDealerDecisionValid"
import { WarrantyDataView } from "src/screens/common/porscheApproved/porscheApprovedPublicOffer"

export const PorscheApprovedVehicleEditContent = ({
	dismissEditMode,
	initialViewState,
	isEditMode,
	saveDealerDecision,
}: {
	dismissEditMode: () => void
	initialViewState: WarrantyPublicOfferView
	isEditMode: boolean
	saveDealerDecision: (approved: boolean) => Promise<void>
}) => {
	const { formatMessage } = useIntl()
	const [isChecked, setIsChecked] = useState(initialViewState.dealerDecisionPorscheApproved)
	const formState = useMemo(
		() => ({ ...initialViewState, dealerDecisionPorscheApproved: isChecked }),
		[initialViewState, isChecked],
	)

	const [isSaving, setIsSaving] = useState(false)
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
	useEffect(() => {
		if (isDealerDecisionValid(formState)) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setErrorMessage(undefined)
		}
	}, [formState, formatMessage, setErrorMessage])

	const onSave = useCallback(async () => {
		if (!isDealerDecisionValid(formState)) {
			setErrorMessage(formatMessage(porscheApprovedMessages.porscheApprovedCertifiedNotAvailable))
			return
		}
		setIsSaving(true)

		try {
			await saveDealerDecision(formState.dealerDecisionPorscheApproved)
			dismissEditMode()
		} finally {
			setIsSaving(false)
		}
	}, [dismissEditMode, formState, formatMessage, saveDealerDecision])

	useEffect(() => {
		if (!isEditMode) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setIsChecked(initialViewState.dealerDecisionPorscheApproved)
			setErrorMessage(undefined)
		}
	}, [isEditMode, initialViewState])

	return (
		<>
			<Container data-testid="warranty-offer-vehicle-details-readonly">
				<WarrantyDataView
					view={formState}
					onDealerDecisionChanged={setIsChecked}
					editMode={isEditMode}
					errorMessage={errorMessage}
				/>
			</Container>

			{isEditMode && (
				<ButtonGroup>
					<PButton loading={isSaving} onClick={onSave}>
						<FormattedMessage {...formMessages.save} />
					</PButton>
					<PButton type="button" variant="secondary" onClick={dismissEditMode}>
						<FormattedMessage {...formMessages.cancel} />
					</PButton>
				</ButtonGroup>
			)}
		</>
	)
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacingStaticMd};
`
